import { useContext, useId, useState } from "react";
import { getProfileName, ThemeWrapper, ViewDetailsModal } from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import apiClient from "../../../../helpers/api-client";
import i18n from "../../../../i18n";
import { CoreButton, CoreSearchField, CoreSearchFieldPaper, CoreSpinner, coreToast } from "@verity/components/core";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { DeleteProfileModal, InstituteProfileModal } from "./profile-modals";
import { ExtraSettingsInfo } from "../../../webpages/settings-root";
import { ProfileTable } from "../shared/profile-table";
import { PortalControllerContext } from "../../../context";
import { UseFetchProctoringDataReturn } from "../useFetchProctoringData";

export type ProfilesEdits = {
	edited: ProctoringOptionProfileV2[];
	deleted: ProctoringOptionProfileV2[];
	added: ProctoringOptionProfileV2[];
};

export const GlobalProfilesInput = (
	props: {
		setting: CustomSettingData<ProfilesEdits>;
		settingsInfo?: Pick<
			Partial<ExtraSettingsInfo>,
			| "Info-instituteProfiles"
			| "Info-instituteSettings"
			| "Info-instituteDisabledProctoringOptions"
			| "Info-instituteAllowStrictMode"
			| "Info-optionRelatedInstituteSettings"
		>;
		onSettingChange: (value?: ProfilesEdits) => void;
		disabled?: boolean;
	} & UseFetchProctoringDataReturn,
) => {
	const { instituteSettings } = useContext(PortalControllerContext);

	const [searchInputFilter, setSearchInputFilter] = useState("");
	const controlId = useId();

	const [deleteProfileModalOpen, setDeleteProfileModalOpen] = useState(false);
	const [createProfileModalOpen, setCreateProfileModalOpen] = useState(false);
	const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
	const [viewProfileModalOpen, setViewProfileModalOpen] = useState(false);

	const [profiles, setProfiles] = useState(
		applyEditsToProfilesList(props.settingsInfo?.["Info-instituteProfiles"] ?? [], props.setting.value),
	);
	const [activeProfile, setActiveProfile] = useState<ProctoringOptionProfileV2>();

	const [lastAddedId, setLastAddedId] = useState(
		props.setting.value?.added.reduce((a, b) => (a > b.id ? b.id : a), 0) ?? 0,
	);
	const [profilesEdits, setProfilesEdits] = useState<ProfilesEdits>(
		props.setting.value ?? { edited: [], deleted: [], added: [] },
	);

	function applyEditsToProfilesList(profilesList: ProctoringOptionProfileV2[], profilesEdits?: ProfilesEdits) {
		if (!profilesEdits) {
			return profilesList;
		}
		const deletedIds = new Map<number, number>(profilesEdits.deleted.map((value, index) => [value.id, index]));
		const addedIds = new Map<number, number>(profilesEdits.added.map((value, index) => [value.id, index]));
		const profileIdToIndex = new Map<number, number>(profilesList.map((value, index) => [value.id, index]));
		const profilesListCopy = profilesList.filter(
			(x) => deletedIds.get(x.id) === undefined && addedIds.get(x.id) === undefined,
		);
		for (const edit of profilesEdits.edited) {
			const index = profileIdToIndex.get(edit.id);
			if (index !== undefined) {
				profilesListCopy[index] = { ...edit };
			}
		}
		return [...profilesListCopy, ...profilesEdits.added];
	}

	function editProfile(profile: ProctoringOptionProfileV2, deleted?: boolean) {
		let editList = profilesEdits.edited;
		let deleteList = profilesEdits.deleted;
		let addList = profilesEdits.added;

		if (profile.id < 0) {
			if (deleted) {
				addList = removeProfileList(addList, profile);
			} else {
				addList = upsertProfileList(addList, profile);
			}
		} else if (deleted) {
			deleteList = upsertProfileList(deleteList, profile);
			editList = removeProfileList(editList, profile);
		} else {
			editList = upsertProfileList(editList, profile);
			deleteList = removeProfileList(deleteList, profile);
		}

		setProfiles(
			applyEditsToProfilesList(props.settingsInfo?.["Info-instituteProfiles"] ?? [], {
				edited: editList,
				deleted: deleteList,
				added: addList,
			}),
		);
		setProfilesEdits({ edited: editList, deleted: deleteList, added: addList });
		props.onSettingChange({ edited: editList, deleted: deleteList, added: addList });
	}

	const displayedProfiles = profiles
		.filter((p) => p.name?.toLowerCase().includes(searchInputFilter.toLowerCase()))
		.sort((a, b) => (a.name ?? "").toLocaleLowerCase().localeCompare((b.name ?? "").toLocaleLowerCase()));

	const optionRelatedInstituteSettings = props.settingsInfo?.["Info-optionRelatedInstituteSettings"];

	const verityInstituteSettings = props.settingsInfo?.["Info-instituteSettings"];

	if (!verityInstituteSettings) {
		return <CoreSpinner />;
	}
	return (
		<ThemeWrapper>
			<CoreButton onClick={() => setCreateProfileModalOpen(true)} aria-haspopup="dialog">
				{i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.createProfile")}
			</CoreButton>

			<CoreSearchFieldPaper sx={{ margin: "14px 0" }}>
				<CoreSearchField
					ariaControls={controlId}
					label={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.searchPlaceholder")}
					onSearch={(value) => setSearchInputFilter(value)}
					totalResults={displayedProfiles.length}
					showSearchButton
					value={searchInputFilter}
				/>
			</CoreSearchFieldPaper>

			<ProfileTable
				id={controlId}
				displayTable={displayedProfiles.length > 0}
				profiles={displayedProfiles}
				emptyTableHeaderText={
					profiles.length === 0
						? i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.noInstProfileCreated")
						: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.noProfileFound")
				}
				dropdownOptions={[
					{
						name: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.view"),
						onClick: (profile) => {
							setActiveProfile(profile);
							setViewProfileModalOpen(true);
						},
					},
					{
						name: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.edit"),
						onClick: (profile) => {
							setActiveProfile(profile);
							setEditProfileModalOpen(true);
						},
					},
					{
						name: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.delete"),
						onClick: (profile) => {
							setActiveProfile(profile);
							setDeleteProfileModalOpen(true);
						},
					},
				]}
				verityInstituteSettings={verityInstituteSettings}
			/>

			{deleteProfileModalOpen && activeProfile && (
				<DeleteProfileModal
					profile={activeProfile}
					deleteProfile={(profile) => {
						editProfile(profile, true);
						coreToast.success(
							i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.profileDeleteSuccess"),
							{ size: "auto" },
						);
					}}
					open={deleteProfileModalOpen}
					onClose={() => {
						setDeleteProfileModalOpen(false);
						setActiveProfile(undefined);
					}}
				/>
			)}
			{createProfileModalOpen && (
				<InstituteProfileModal
					editProfile={(profile) => editProfile(profile)}
					lastAddedId={lastAddedId}
					setLastAddedId={setLastAddedId}
					verityInstituteSettings={verityInstituteSettings}
					instituteSettings={optionRelatedInstituteSettings ?? instituteSettings}
					open={createProfileModalOpen}
					onClose={() => {
						setCreateProfileModalOpen(false);
						setActiveProfile(undefined);
					}}
					applications={props.applications}
					extensions={props.extensions}
				/>
			)}
			{editProfileModalOpen && activeProfile && (
				<InstituteProfileModal
					profile={activeProfile}
					editProfile={(profile) => editProfile(profile)}
					verityInstituteSettings={verityInstituteSettings}
					instituteSettings={optionRelatedInstituteSettings ?? instituteSettings}
					open={editProfileModalOpen}
					onClose={() => {
						setEditProfileModalOpen(false);
						setActiveProfile(undefined);
					}}
					applications={props.applications}
					extensions={props.extensions}
				/>
			)}
			{viewProfileModalOpen && activeProfile && (
				<ViewDetailsModal
					profile={activeProfile}
					title={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.proctoringProfile", {
						name: getProfileName(activeProfile),
					})}
					configuration={{
						optionRelatedInstituteSettings,
						verityInstituteSettings,
						proctoringData: { applications: props.applications, extensions: props.extensions },
						proctoringOption: activeProfile.proctoringOption,
					}}
					onClose={() => {
						setViewProfileModalOpen(false);
						setActiveProfile(undefined);
					}}
				/>
			)}
		</ThemeWrapper>
	);
};

function upsertProfileList(list: ProctoringOptionProfileV2[], profileToUpsert: ProctoringOptionProfileV2) {
	const index = list.findIndex((x) => x.id === profileToUpsert.id);
	if (index > -1) {
		list[index] = profileToUpsert;
	} else {
		list.push(profileToUpsert);
	}
	return list;
}

function removeProfileList(list: ProctoringOptionProfileV2[], profileToRemove: ProctoringOptionProfileV2) {
	return list.filter((x) => x.id !== profileToRemove.id);
}

export async function saveGlobalProfiles(profilesEdits?: ProfilesEdits) {
	try {
		if (profilesEdits) {
			const addPromiseList = profilesEdits.added.map((profile) =>
				apiClient.proctoringProfile.create({
					proctoringOption: profile.proctoringOption,
					isInstituteProfile: true,
					name: profile.name ?? undefined,
				}),
			);
			const editPromiseList = profilesEdits.edited.map((profile) =>
				apiClient.proctoringProfile.update({
					id: profile.id,
					proctoringOption: profile.proctoringOption,
					name: profile.name ?? undefined,
				}),
			);
			const deletePromiseList = profilesEdits.deleted.map((profile) => apiClient.proctoringProfile.delete(profile.id));
			await Promise.all([...addPromiseList, ...editPromiseList, ...deletePromiseList]);
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
		return { ok: false, reason: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.notSave") };
	}
}
